import Handlebars from 'handlebars';
import parseISO from 'date-fns/parseISO';
import { Language, LanguageLocales } from '../utils/system-languages';
import { ProfileInteractionPayload } from './types/interaction';
import { htmlSanitize } from '../utils/html-sanitize';

function formatEventTimestamp(iso: string, locale: string): string {
  return parseISO(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
}

export function toProfileInteractionDetailViewModel(
  payload: ProfileInteractionPayload,
  locale: string,
): ProfileInteractionPayload {
  return {
    ...payload,
    eventTimestamp: formatEventTimestamp(payload.eventTimestamp, locale),
    details: payload.details.map(detail => ({
      ...detail,
      eventTimestamp: formatEventTimestamp(detail.eventTimestamp, locale),
    })),
  };
}

/**
 * Compile a Handlebars detail template against a profile-interaction payload.
 * Throws on Handlebars syntax/runtime errors so callers can surface diagnostics (e.g. BO preview).
 */
export function compileInteractionDetailTemplate(
  template: string,
  payload: ProfileInteractionPayload,
  language: Language = Language.en,
): string {
  if (!template?.trim()) {
    return '';
  }

  const locale = LanguageLocales[language] ?? LanguageLocales[Language.en];
  return htmlSanitize(
    Handlebars.compile(template)(toProfileInteractionDetailViewModel(payload, locale)),
  );
}

/**
 * Render `payload.detailTemplate` for display (portal / CRM).
 * Returns an empty string when the template is missing or compilation fails.
 */
export function renderProfileInteractionDetailHtml(
  payload: ProfileInteractionPayload,
  language: Language = Language.en,
): string {
  try {
    return compileInteractionDetailTemplate(payload.detailTemplate ?? '', payload, language);
  } catch {
    return '';
  }
}
